import type { DistributorItem } from "./distributor-find";
import { detectDistributorType, getFilteredSkillIds } from "./distributor-type";
import { renderDistributorSkillIcon } from "./distributor-render-skill-icon";
import {
  appendOptionalElements,
  renderDivContainer,
} from "./distributor-render-info-window";
import { renderAdditionalIconsForDistributor } from "./distributor-additional-icons";

/**
 * Renders a list item for the given location
 */
export const renderListItemForDistributorLocation = (
  location: DistributorItem,
  lastAddressQuery: string
): HTMLDivElement => {
  const mapContainer = document.getElementById("distributor-search"),
    mapLocation = document.createElement("div");
  mapLocation.classList.add("map__sidebar-location");
  mapLocation.dataset["id"] = location.Id;

  // Name section
  if (location.Name) {
    const nameSection = document.createElement("div"),
      nameNode = document.createElement("strong");

    if (!location.Additional_Name__c) {
      nameSection.classList.add("padding-bottom--s");
    }
    nameNode.innerHTML = location.Name;

    nameSection.appendChild(nameNode);
    mapLocation.appendChild(nameSection);
  }

  // Additional name section
  if (location.Additional_Name__c) {
    const additionalNameSection = document.createElement("div"),
      additionalNameNode = document.createTextNode(location.Additional_Name__c);

    additionalNameSection.classList.add("padding-bottom--s");

    additionalNameSection.appendChild(additionalNameNode);
    mapLocation.appendChild(additionalNameSection);
  }

  // Visit Street Section
  if (location.Visit_Street__c) {
    const visitStreetSection = document.createElement("div"),
      streetNode = document.createTextNode(location.Visit_Street__c);

    visitStreetSection.appendChild(streetNode);

    mapLocation.appendChild(visitStreetSection);
  }

  // Address section
  if (
    location.Visit_Zip_Code__c ||
    location.Visit_City__c ||
    location.Visit_Zip_Code__c ||
    location.Visit_Country__c
  ) {
    const addressSection = document.createElement("div"),
      addressNode = document.createElement("span");

    const hideCountry = mapContainer?.dataset["hideCountry"] == "1";

    if (location.Visit_Zip_Code__c)
      addressNode.innerHTML += " " + location.Visit_Zip_Code__c;
    if (location.Visit_City__c)
      addressNode.innerHTML += " " + location.Visit_City__c;
    if (location.Visit_Country__c && !hideCountry) {
      if (location.Visit_Zip_Code__c || location.Visit_City__c)
        addressNode.innerHTML += ",";
      addressNode.innerHTML += " " + location.Visit_Country__c;
    }

    addressSection.appendChild(addressNode);
    mapLocation.appendChild(addressSection);
  }

  // 24/7 Phone section
  if (
    detectDistributorType(location) === "workshop24" &&
    typeof location.Emergency_Phone_Number__c === "string"
  ) {
    const emergencyPhoneSection = document.createElement("div");
    const emergencyPhoneLink = document.createElement("a");

    emergencyPhoneLink.href = `tel:${location.Emergency_Phone_Number__c}`;
    emergencyPhoneLink.classList.add("margin-left--xxs");
    emergencyPhoneLink.append(
      document.createTextNode(location.Emergency_Phone_Number__c)
    );

    emergencyPhoneSection.append(
      document.createTextNode(mapContainer?.dataset["emergencyPhone"] ?? "")
    );
    emergencyPhoneSection.append(emergencyPhoneLink);
    mapLocation.append(emergencyPhoneSection);
  }

  // Phone section
  if (location.Phone) {
    const phoneSection = document.createElement("div"),
      phoneLink = document.createElement("a");

    phoneLink.setAttribute("href", "tel:" + location.Phone);
    phoneLink.classList.add("margin-left--xxs");
    phoneLink.innerHTML = location.Phone;

    phoneSection.appendChild(
      document.createTextNode(mapContainer?.dataset["phone"] ?? "")
    );
    phoneSection.appendChild(phoneLink);
    mapLocation.appendChild(phoneSection);
  }

  // Website section
  if (location.Website) {
    const websiteSection = document.createElement("div"),
      websiteLink = document.createElement("a");

    const link = location.Website.replace(/(^\w+:|^)\/\//, '');
    websiteLink.setAttribute("href",  `//${link}`);
    websiteLink.setAttribute("target", "_blank");
    websiteLink.setAttribute("title", location.Name);
    websiteLink.innerHTML = location.Website;

    websiteSection.appendChild(websiteLink);
    mapLocation.appendChild(websiteSection);
  }

  const skillIds = getFilteredSkillIds(location);

  // Skills section
  if (location.Additional_Skills_Service_Partners__c && skillIds.length > 0) {
    const skillsSection = document.createElement("div");
    skillsSection.classList.add("map__info__skills");

    for (const skillId of skillIds) {
      const skillTitle = mapContainer?.dataset[skillId];

      // go to next skill if name and image are not set
      if (!skillTitle) {
        continue;
      }

      const skillItem = document.createElement("div");
      skillItem.classList.add("map__info__skills__item");
      skillItem.classList.add("margin-top--xs");

      skillItem.title = skillTitle;

      skillItem.appendChild(renderDistributorSkillIcon(skillId, skillTitle));
      skillsSection.appendChild(skillItem);
    }

    mapLocation.appendChild(skillsSection);
  }

  return appendOptionalElements(
    mapLocation,
    renderLowerPart(
      location,
      mapContainer?.dataset["direction"] ?? "Directions",
      lastAddressQuery
    )
  );
};

/**
 * the directions button next to the additional distributor location icons.
 */
const renderLowerPart = (
  location: DistributorItem,
  directionButtonLabel: string,
  lastAddressQuery: string
) =>
  appendOptionalElements(
    renderDivContainer("map__info__lower-part"),
    renderRouteLink(location, directionButtonLabel, lastAddressQuery),
    renderAdditionalIconsForDistributor(location)
  );

/**
 * render a button to the google maps directions to the distributor
 */
const renderRouteLink = (
  location: DistributorItem,
  directionButtonLabel: string,
  lastAddressQuery: string
) => {
  if (
    !location.Visit_Street__c ||
    !location.Visit_City__c ||
    !location.Visit_Zip_Code__c
  ) {
    return null;
  }

  const googleRouteLinkSection = document.createElement("div"),
    googleLink = document.createElement("a"),
    targetAddress =
      location.Visit_Street__c +
      " " +
      location.Visit_City__c +
      " " +
      location.Visit_Zip_Code__c;

  googleLink.setAttribute(
    "href",
    "https://maps.google.com/maps?saddr=" +
    encodeURIComponent(lastAddressQuery) +
    "&daddr=" +
    encodeURIComponent(targetAddress)
  );
  googleLink.append(directionButtonLabel);
  googleLink.setAttribute("target", "_blank");
  googleLink.classList.add("map__directions", "link", "link--primary");

  googleRouteLinkSection.appendChild(googleLink);
  return googleRouteLinkSection;
};
